import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Badge, Form, Image, Button } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaShoppingCart, FaHeart, FaShare, FaUser, FaUniversity, FaStar, FaMapMarkerAlt, 
  FaInfoCircle, FaQuestionCircle, FaTruck, FaShieldAlt, FaPlus, FaMinus, FaCheck, 
  FaTag, FaCalendarAlt, FaEye, FaRegStar, FaStarHalfAlt, FaClock, FaBox, 
  FaRegClock, FaRegCalendarAlt, FaRegCommentAlt, FaEnvelope, FaStore, FaMoneyBillWave,
  FaCheckCircle, FaShippingFast, FaAward
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Layout from '../components/layout/Layout';
// import ProductReviews from '../components/product/ProductReviews';
import ProductQA from '../components/product/ProductQA';
import RelatedProducts from '../components/product/RelatedProducts';
import ProductDetailSkeleton from '../components/product/ProductDetailSkeleton';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useWishlist } from '../contexts/WishlistContext';
import { useMessages } from '../contexts/MessageContext';
import api from '../services/api';
import './ProductDetail.css';

const QuantitySelector = ({ quantity, setQuantity }) => (
  <div className="quantity-selector">
    <button type="button" className="quantity-btn" onClick={() => setQuantity(Math.max(1, quantity - 1))}>
      <FaMinus />
    </button>
    <input 
      type="text" 
      className="quantity-input" 
      value={quantity} 
      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
      aria-label="Quantity"
    />
    <button type="button" className="quantity-btn" onClick={() => setQuantity(quantity + 1)}>
      <FaPlus />
    </button>
  </div>
);

const ProductDetail = () => {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const { addToCart } = useCart();
  const { addToWishlist, isInWishlist, removeFromWishlist } = useWishlist();
  const { sendMessage, createConversation, conversations } = useMessages();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('description');
  const [isWishlisted, setIsWishlisted] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/products/${id}`);
        const fetchedProduct = response.data; // Backend returns product directly
        if (fetchedProduct) {
          setProduct(fetchedProduct);
          setError(null);
          setIsWishlisted(isInWishlist(fetchedProduct.id));
        } else {
          setError('Product not found.');
          setProduct(null);
        }
      } catch (err) {
        setError('Failed to fetch product details.');
        console.error(err);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, isInWishlist]);

  const handleAddToCart = () => {
    if (product) {
      addToCart(product, quantity);
      toast.success(`${product.name} added to cart!`, {
        position: "bottom-right",
        autoClose: 3000
      });
    }
  };

  const handleWishlistToggle = () => {
    if (!product) return;
    
    if (isWishlisted) {
      removeFromWishlist(product.id);
      setIsWishlisted(false);
      toast.info(`${product.name} removed from wishlist.`, {
        position: "bottom-right",
        autoClose: 3000
      });
    } else {
      addToWishlist(product);
      setIsWishlisted(true);
      toast.success(`${product.name} added to wishlist!`, {
        position: "bottom-right",
        autoClose: 3000
      });
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: product.name,
        text: `Check out this product: ${product.name}`,
        url: url
      })
      .then(() => toast.success('Shared successfully!'))
      .catch((error) => console.log('Error sharing', error));
    } else {
      navigator.clipboard.writeText(url)
        .then(() => {
          toast.success('Link copied to clipboard!', {
            position: "bottom-right",
            autoClose: 3000
          });
        })
        .catch(err => {
          console.error('Failed to copy link: ', err);
        });
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      toast.error('Please login to send messages.', {
        position: "bottom-right",
        autoClose: 3000
      });
      return;
    }
    
    if (!message.trim()) {
      toast.error('Please enter a message.', {
        position: "bottom-right",
        autoClose: 3000
      });
      return;
    }
    
    try {
      // Find existing thread for this buyer, seller, and product
      let thread = conversations.find(
        t => t.seller.id === product.seller.id && t.product?.id === product.id
      );
      let threadId = thread ? thread.id : null;
      if (!threadId) {
        threadId = await createConversation(product.seller.id, message, product.id);
        // Optionally refetch conversations here if needed
      } else {
        await sendMessage(threadId, message);
      }
      setMessage('');
      toast.success('Message sent successfully!', {
        position: "bottom-right",
        autoClose: 3000
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.', {
        position: "bottom-right",
        autoClose: 3000
      });
      console.error(error);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  if (loading) {
    return (
      <Layout>
        <Container className="product-detail-page">
          <ProductDetailSkeleton />
        </Container>
      </Layout>
    );
  }

  if (error || !product) {
    return (
      <Layout>
        <Container className="product-detail-page">
          <div className="error-container">
            <h2>Oops! Something went wrong</h2>
            <p>{error || 'Product not found'}</p>
            <Link to="/products" className="btn btn-primary">
              Back to Products
            </Link>
          </div>
        </Container>
      </Layout>
    );
  }

  // Calculate rating stats
  const ratings = {
    5: 65,
    4: 20,
    3: 10,
    2: 3,
    1: 2
  };
  
  const totalRatings = Object.values(ratings).reduce((a, b) => a + b, 0);
  const avgRating = (5*ratings[5] + 4*ratings[4] + 3*ratings[3] + 2*ratings[2] + 1*ratings[1]) / totalRatings;

  const sellerName =
    product.seller?.first_name ||
    product.seller?.name ||
    'UniTrade Seller';
  const sellerUniversity = product.seller?.university?.name;
  const sellerCampus = product.seller?.campus?.name;

  // Function to strip HTML tags from description
  const stripHtmlTags = (html) => {
    if (!html || typeof html !== 'string') return '';
    try {
      const withoutTags = html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ');
      return withoutTags.replace(/\s+/g, ' ').trim();
    } catch (e) {
      return html;
    }
  };

  return (
    <Layout>
      <motion.div 
        className="product-detail-hero"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Container>
          <div className="breadcrumb-wrapper">
            <Link to="/">Home</Link> / <Link to="/products">Products</Link> / <span>{product.name}</span>
          </div>
        </Container>
      </motion.div>
      
      <Container className="product-detail-page">
        {/* Product Overview Section */}
        <div className="product-overview-section">
          <Row>
            {/* Left Column - Image Gallery */}
            <Col lg={6} className="product-gallery mb-4">
              <motion.div 
                className="main-image-wrapper"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <motion.img
                  key={selectedImage}
                  src={product.images && product.images.length > 0 ? product.images[selectedImage].image : 'https://via.placeholder.com/600x600?text=No+Image'}
                  alt={product.name}
                  className="main-product-image"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />
                <motion.button 
                  className={`wishlist-btn ${isWishlisted ? 'active' : ''}`}
                  onClick={handleWishlistToggle}
                  aria-label="Add to wishlist"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <motion.div
                    animate={{ scale: isWishlisted ? [1, 1.2, 1] : 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <FaHeart />
                  </motion.div>
                </motion.button>
              </motion.div>
              
              <motion.div 
                className="thumbnail-reel"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {product.images && product.images.map((image, index) => (
                  <motion.img
                    key={index}
                    src={image.image}
                    alt={`${product.name} - view ${index + 1}`}
                    className={`thumbnail-img ${selectedImage === index ? 'active' : ''}`}
                    onClick={() => setSelectedImage(index)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  />
                ))}
              </motion.div>

              {/* Seller Info - Moved under images */}
              <motion.div 
                className="seller-info-card mt-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div className="card-header">
                  <FaStore /> Seller Information
                </div>
                <div className="card-body">
                  <div className="seller-profile">
                    <div className="seller-avatar">
                      <FaUser />
                    </div>
                    <div className="seller-details">
                      <h4 className="seller-name">{sellerName}</h4>
                      <div className="seller-type">
                        {product.seller?.type === 'student' ? (
                          <><FaUniversity /> Student seller</>
                        ) : (
                          <><FaStore /> Seller</>
                        )}
                      </div>
                      <ul className="seller-details-list">
                        {sellerUniversity && (
                          <li>
                            <FaUniversity />
                            <span>
                              {sellerUniversity}
                              {sellerCampus ? ` • ${sellerCampus}` : ''}
                            </span>
                          </li>
                        )}
                        <li>
                          <FaMapMarkerAlt />
                          <span>Campus pickup preferred</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="seller-stats mt-3">
                    <div className="stat-item">
                      <FaRegClock /> Response Time: <strong>Within 2 hours</strong>
                    </div>
                  </div>

                  <Form onSubmit={handleSendMessage} className="mt-3">
                    <Form.Group>
                      <Form.Label><FaEnvelope /> Message Seller</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Ask a question about this product..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                      />
                    </Form.Group>
                    <Button 
                      type="submit" 
                      variant="outline-primary" 
                      className="mt-2 w-100 message-seller-btn"
                      disabled={!currentUser}
                    >
                      Send Message
                    </Button>
                    {!currentUser && (
                      <small className="text-muted d-block mt-2">
                        Please <Link to="/login">login</Link> to contact the seller
                      </small>
                    )}
                  </Form>
                </div>
              </motion.div>
            </Col>
            
            {/* Right Column - Product Info */}
            <Col lg={6}>
              <motion.div 
                className="product-info-wrapper"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                {/* Product Header */}
                <motion.div 
                  className="product-header"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <div className="product-badges">
                    <Badge bg={product.condition === 'new' ? 'success' : 'info'} className="badge-condition">
                      <FaTag /> {product.condition === 'new' ? 'New' : 'Used'}
                    </Badge>
                    {/* Commented out - backend doesn't provide these fields
                    {product.isOnSale && (
                      <Badge bg="danger" className="badge-sale">
                        <FaTag /> Sale
                      </Badge>
                    )}
                    {product.isFeatured && (
                      <Badge bg="primary" className="badge-featured">
                        <FaStar /> Featured
                      </Badge>
                    )}
                    */}
                  </div>
                  
                  <h1 className="product-name">{product.name}</h1>
                  
                  <div className="product-meta">
                    <div className="product-rating">
                      <div className="stars">
                        {[...Array(5)].map((_, i) => (
                          <span key={i}>
                            {avgRating >= i + 1 ? (
                              <FaStar className="filled" />
                            ) : avgRating >= i + 0.5 ? (
                              <FaStarHalfAlt className="filled" />
                            ) : (
                              <FaRegStar />
                            )}
                          </span>
                        ))}
                      </div>
                      <span className="rating-value">{avgRating.toFixed(1)}</span>
                      <span className="rating-count">({totalRatings} reviews)</span>
                    </div>
                  </div>
                </motion.div>
                
                {/* Price Section */}
                <motion.div 
                  className="product-pricing"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <span className="price-label">
                    <FaMoneyBillWave className="me-2" />
                    Price
                  </span>
                  <div className="product-price-wrapper">
                    <div className="current-price">
                      <span className="currency">GH₵</span>
                      {Number(product.price).toFixed(2)}
                    </div>
                    {/* Commented out - backend doesn't provide originalPrice
                    {product.originalPrice && product.originalPrice > product.price && (
                      <>
                        <div className="original-price">
                          <span className="currency">GH₵</span>
                          {product.originalPrice.toFixed(2)}
                        </div>
                        <div className="discount-badge">
                          <FaTag />
                          {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}% OFF
                        </div>
                      </>
                    )}
                    */}
                  </div>
                  
                  <div className="stock-info">
                    <div className={`stock-status ${product.stockCount > 10 ? 'in-stock' : product.stockCount > 0 ? 'low-stock' : 'out-of-stock'}`}>
                      <FaBox />
                      {product.stockCount > 10 ? 'In Stock' : product.stockCount > 0 ? 'Low Stock' : 'Out of Stock'}
                    </div>
                    <div className="stock-progress">
                      <div className={`stock-progress-bar ${
                        product.stockCount > 10 ? 'stock-progress-high' : 
                        product.stockCount > 5 ? 'stock-progress-medium' : 
                        'stock-progress-low'
                      }`}></div>
                    </div>
                  </div>
                  
                  <ul className="product-features">
                    <motion.li
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      <FaCheckCircle className="feature-icon" /> {product.features?.[0] || 'Authentic product'}
                    </motion.li>
                    <motion.li
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.45 }}
                    >
                      <FaCheckCircle className="feature-icon" /> {product.features?.[1] || 'Verified seller'}
                    </motion.li>
                    <motion.li
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 }}
                    >
                      <FaCheckCircle className="feature-icon" /> {product.features?.[2] || 'Student discount eligible'}
                    </motion.li>
                  </ul>
                </motion.div>
                
                {/* Purchase Actions */}
                <motion.div 
                  className="product-actions"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.55 }}
                >
                  <QuantitySelector quantity={quantity} setQuantity={setQuantity} />
                  <motion.button 
                    className="add-to-cart-btn" 
                    onClick={handleAddToCart}
                    disabled={!product.stockCount}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <FaShoppingCart />
                    Add to Cart
                  </motion.button>
                </motion.div>
                
                <motion.div 
                  className="action-buttons-secondary"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <motion.button 
                    className="action-btn wishlist-action-btn" 
                    onClick={handleWishlistToggle}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <FaHeart />
                    {isWishlisted ? 'Remove from Wishlist' : 'Add to Wishlist'}
                  </motion.button>
                  <motion.button 
                    className="action-btn" 
                    onClick={handleShare}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <FaShare />
                    Share
                  </motion.button>
                </motion.div>
                
                {/* Delivery Info */}
                <motion.div 
                  className="delivery-info"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.65 }}
                >
                  <div className="delivery-info-title">
                    <FaShippingFast />
                    Delivery Options
                  </div>
                  <div className="delivery-options">
                    <div className="delivery-option">
                      <div className="delivery-option-icon">
                        <FaMapMarkerAlt />
                      </div>
                      <div className="delivery-option-content">
                        <h4>Campus Pickup</h4>
                        <p>Free • 1-2 days</p>
                      </div>
                    </div>
                    <div className="delivery-option">
                      <div className="delivery-option-icon">
                        <FaTruck />
                      </div>
                      <div className="delivery-option-content">
                        <h4>Local Delivery <span style={{ textDecoration: 'line-through', color: '#888', marginLeft: 8 }}>GH₵50</span></h4>
                        <p style={{ textDecoration: 'line-through', color: '#888' }}>Arranged by seller (Currently not available)</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </Col>
          </Row>
        </div>
        
        {/* Product Details Section */}
        <motion.div 
          className="product-details-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="product-details-tabs">
            <button 
              className={`tab-button ${activeTab === 'description' ? 'active' : ''}`}
              onClick={() => handleTabChange('description')}
            >
              Description
            </button>
            <button 
              className={`tab-button ${activeTab === 'specifications' ? 'active' : ''}`}
              onClick={() => handleTabChange('specifications')}
            >
              Specifications
            </button>
            <button 
              className={`tab-button ${activeTab === 'shipping' ? 'active' : ''}`}
              onClick={() => handleTabChange('shipping')}
            >
              Shipping & Returns
            </button>
          </div>
          
          <div className="tab-content-wrapper">
            <div className={`tab-content ${activeTab === 'description' ? 'active' : ''}`}>
              <div className="product-description">
                <p>{stripHtmlTags(product.description) || 'No description available for this product.'}</p>
                <h3>Key Features</h3>
                <ul className="feature-list">
                  {product.features ? (
                    product.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))
                  ) : (
                    <>
                      <li>High quality product</li>
                      <li>Perfect for students</li>
                      <li>Great value for money</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
            
            <div className={`tab-content ${activeTab === 'specifications' ? 'active' : ''}`}>
              <table className="specifications-table">
                <tbody>
                  <tr>
                    <th>Condition</th>
                    <td>{product.condition === 'new' ? 'New' : 'Used'}</td>
                  </tr>
                  <tr>
                    <th>Category</th>
                    <td>{product.category}</td>
                  </tr>
                  <tr>
                    <th>Listed On</th>
                    <td>{new Date(product.createdAt).toLocaleDateString()}</td>
                  </tr>
                  {/* Commented out - backend doesn't provide these fields
                  <tr>
                    <th>Brand</th>
                    <td>{product.brand || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Model</th>
                    <td>{product.model || 'N/A'}</td>
                  </tr>
                  {product.year && (
                    <tr>
                      <th>Year</th>
                      <td>{product.year}</td>
                    </tr>
                  )}
                  {product.specifications && Object.entries(product.specifications).map(([key, value]) => (
                    <tr key={key}>
                      <th>{key}</th>
                      <td>{value}</td>
                    </tr>
                  ))}
                  */}
                </tbody>
              </table>
            </div>
            
            <div className={`tab-content ${activeTab === 'shipping' ? 'active' : ''}`}>
              <div className="shipping-info">
                <h3>Shipping & Delivery</h3>
                <div className="shipping-option">
                  <FaTruck className="shipping-icon" />
                  <div className="shipping-details">
                    <h4>Campus Pickup</h4>
                    <p>Meet the seller on campus at a safe, public location.</p>
                    <p>Typically arranged within 1-2 days of purchase.</p>
                  </div>
                </div>
                
                <h3 className="mt-4">Return Policy</h3>
                <p>All sales are final. Please inspect the item before purchasing.</p>
                <p>Contact the seller directly with any questions before making your purchase.</p>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Related Products Section */}
        <motion.div 
          className="related-products-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <h2 className="section-title">
            <FaAward className="me-2" />
            Related Products
          </h2>
          <div className="products-carousel">
            <RelatedProducts category={product.category} currentProductId={product.id} />
          </div>
        </motion.div>
      </Container>
    </Layout>
  );
};

export default ProductDetail;
